import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Award,
  Users,
  Clock,
  ChevronDown,
  ChevronUp,
  Star,
  CheckCircle,
  Target,
  Eye,
} from "lucide-react";
import { useBookingModal } from "../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const { openModal } = useBookingModal();

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const features = [
    {
      icon: Shield,
      title: t("1377", "Safety First"),
      description: t(
        "1378",
        "Professional drivers with years of experience and fully insured vehicles"
      ),
    },
    {
      icon: Clock,
      title: t("1379", "Always On Time"),
      description: t(
        "1380",
        "Punctual service you can depend on, 24/7 availability"
      ),
    },
    {
      icon: Award,
      title: t("1381", "Premium Quality"),
      description: t(
        "1382",
        "Mercedes-Benz fleet with latest technology and luxury amenities"
      ),
    },
    {
      icon: Users,
      title: t("1383", "Customer Focused"),
      description: t(
        "1384",
        "Dedicated to exceptional service and customer satisfaction"
      ),
    },
  ];

  const stats = [
    { number: "32,002", label: t("1385", "Special Vehicles") },
    { number: "16,008", label: t("1386", "Road Trips Completed") },
    { number: "17,068", label: t("1387", "People Transported") },
    { number: "5", label: t("1388", "Star Rating") },
  ];

  return (
    <div className="pt-20 bg-gray-900 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/about.webp"
            alt={t("1389", "About TaxiSchipholAirport")}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-linear-to-r from-gray-900/90 to-gray-800/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
            >
              <Award className="h-4 w-4" />
              <span>{t("1390", "About TaxiSchipholAirport")}</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              {t("1391", "Your Reliable")}{" "}
              <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {t("1392", "Transportation")}
              </span>{" "}
              {t("1393", "Partner")}
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              {t(
                "1394",
                "Welcome to TaxiSchipholAirport, your reliable and efficient transportation solution for travel to and from Schiphol Airport."
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main About Content */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  {t("1395", "Excellence in Every Journey")}
                </h2>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  {t(
                    "1396",
                    "At TaxiSchipholAirport, we pride ourselves on providing our customers with safe, comfortable, and timely transportation services. Our team of experienced and professional drivers is dedicated to ensuring that you arrive at your destination on time and in style."
                  )}
                </p>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  {t(
                    "1397",
                    "Whether you're traveling for business or pleasure, we're here to make your journey as smooth and stress-free as possible. We offer a range of transportation options to suit your needs, including airport transfers, taxi services, and corporate transportation."
                  )}
                </p>
                <p className="text-lg text-gray-200 leading-relaxed">
                  {t(
                    "1398",
                    "Our vehicles are well-maintained, comfortable, and equipped with the latest technology to ensure a comfortable and enjoyable ride. With our competitive pricing and commitment to quality service, we're confident that you'll be satisfied with your experience with TaxiSchipholAirport."
                  )}
                </p>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="bg-gray-800/50 backdrop-blur-xs p-4 rounded-xl border border-gray-700"
                  >
                    <feature.icon className="h-8 w-8 text-primary mb-3" />
                    <div className="font-semibold text-white text-sm mb-2">
                      {feature.title}
                    </div>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      {feature.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img
                  src="/images/ab.webp"
                  alt={t("1399", "Professional taxi service")}
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />

                {/* Floating Stats */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="absolute bottom-6 left-6 right-6 bg-gray-800/95 backdrop-blur-xs p-6 rounded-2xl"
                >
                  <div className="grid grid-cols-2 gap-4">
                    {stats.map((stat) => (
                      <div key={stat.label} className="text-center">
                        <div className="text-2xl font-bold text-primary mb-1">
                          {stat.number}
                        </div>
                        <div className="text-xs text-gray-300">
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Sections */}
      <section className="py-24 bg-gray-800/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("1400", "Our Vision & Mission")}
            </h2>
            <p className="text-xl text-gray-300">
              {t(
                "1401",
                "Discover what drives us to provide exceptional transportation services"
              )}
            </p>
          </motion.div>

          {/* Vision Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <button
              onClick={() => toggleSection("vision")}
              className="w-full bg-gray-800/50 backdrop-blur-xs border border-gray-700 rounded-2xl p-6 text-left hover:bg-gray-700/50 transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-linear-to-r from-primary to-primary-light rounded-xl flex items-center justify-center">
                    <Eye className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {t("1402", "Our Vision")}
                    </h3>
                    <p className="text-gray-300">
                      {t("1403", "Redefining airport transportation")}
                    </p>
                  </div>
                </div>
                {expandedSection === "vision" ? (
                  <ChevronUp className="h-6 w-6 text-primary" />
                ) : (
                  <ChevronDown className="h-6 w-6 text-primary" />
                )}
              </div>
            </button>

            <motion.div
              initial={false}
              animate={{
                height: expandedSection === "vision" ? "auto" : 0,
                opacity: expandedSection === "vision" ? 1 : 0,
              }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="p-6 bg-gray-800/30 rounded-b-2xl border-x border-b border-gray-700 space-y-4">
                <p className="text-gray-200 leading-relaxed">
                  {t(
                    "1404",
                    "At Taxischipholairport.net, our vision is to redefine the way people experience airport transportation. We aspire to be the leading provider of reliable, efficient, and customer-focused taxi services to and from Schiphol Airport, delivering an experience that goes beyond simple transportation."
                  )}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  {[
                    {
                      title: t("1408", "Innovation and Efficiency"),
                      description: t(
                        "1409",
                        "Leverage the latest technology to enhance passenger experience with seamless booking systems and real-time tracking."
                      ),
                    },
                    {
                      title: t("1410", "Sustainability"),
                      description: t(
                        "1411",
                        "Committed to exploring eco-friendly practices and transitioning to electric vehicles for a greener future."
                      ),
                    },
                    {
                      title: t("1412", "Global Standard"),
                      description: t(
                        "1413",
                        "Provide unmatched customer service tailored to meet the needs of every traveler with exceptional hospitality."
                      ),
                    },
                    {
                      title: t("1414", "Growth & Expansion"),
                      description: t(
                        "1415",
                        "Expand services to other key locations, making us synonymous with excellence in airport transportation across Europe."
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="flex items-start space-x-3"
                    >
                      <CheckCircle className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <div>
                        <div className="font-semibold text-white mb-1">
                          {item.title}
                        </div>
                        <p className="text-sm text-gray-300">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Mission Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <button
              onClick={() => toggleSection("mission")}
              className="w-full bg-gray-800/50 backdrop-blur-xs border border-gray-700 rounded-2xl p-6 text-left hover:bg-gray-700/50 transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-linear-to-r from-accent to-primary rounded-xl flex items-center justify-center">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {t("1405", "Our Mission")}
                    </h3>
                    <p className="text-gray-300">
                      {t(
                        "1406",
                        "Exceptional travel experience for every passenger"
                      )}
                    </p>
                  </div>
                </div>
                {expandedSection === "mission" ? (
                  <ChevronUp className="h-6 w-6 text-primary" />
                ) : (
                  <ChevronDown className="h-6 w-6 text-primary" />
                )}
              </div>
            </button>

            <motion.div
              initial={false}
              animate={{
                height: expandedSection === "mission" ? "auto" : 0,
                opacity: expandedSection === "mission" ? 1 : 0,
              }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="p-6 bg-gray-800/30 rounded-b-2xl border-x border-b border-gray-700 space-y-4">
                <p className="text-gray-200 leading-relaxed">
                  {t(
                    "1407",
                    "At Taxischipholairport.net, we firmly believe that every journey should begin and end with ease, comfort, and peace of mind. Our mission is not only to transport you from point A to point B, but to provide you with an exceptional travel experience that starts the moment you step into one of our vehicles."
                  )}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  {[
                    {
                      title: t("1416", "Convenience at Your Fingertips"),
                      description: t(
                        "1417",
                        "User-friendly booking system that makes reserving your ride as easy as a few clicks, whether in advance or last-minute."
                      ),
                    },
                    {
                      title: t("1418", "Reliability You Can Count On"),
                      description: t(
                        "1419",
                        "Punctual service with experienced drivers who know the quickest routes and track flight details for perfect timing."
                      ),
                    },
                    {
                      title: t("1420", "Affordability Without Compromise"),
                      description: t(
                        "1421",
                        "Transparent, competitive pricing free from hidden fees, ensuring quality service available to everyone."
                      ),
                    },
                    {
                      title: t("1422", "Safety First, Always"),
                      description: t(
                        "1423",
                        "Regularly maintained vehicles adhering to highest safety standards with trained professional drivers."
                      ),
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="flex items-start space-x-3"
                    >
                      <Star className="h-5 w-5 text-warning mt-1 shrink-0" />
                      <div>
                        <div className="font-semibold text-white mb-1">
                          {item.title}
                        </div>
                        <p className="text-sm text-gray-300">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-linear-to-r from-primary via-primary-light to-primary rounded-3xl p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t("1424", "Ready to Experience Premium Transportation?")}
              </h2>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                {t(
                  "1425",
                  "Join thousands of satisfied customers who trust TaxiSchipholAirport for their transportation needs"
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openModal}
                  className="bg-white text-primary px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg"
                >
                  {t("1426", "Book Your Ride Now")}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300"
                >
                  {t("1427", "Contact Us")}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
